import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  User, Mail, Phone, School, BookOpen, Calendar, Clock, ShieldCheck, Loader2,
  KeyRound, Save, Trash2, AlertTriangle, CheckCircle2, ArrowRight, Chrome, Palette, RefreshCcw, LogOut,
} from "lucide-react";
import { Link } from "react-router-dom";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
};
const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
};

export default function StudentProfile() {
  const { setUser, logout } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit form state
  const [form, setForm] = useState({ full_name: "", contact_number: "", school: "" });

  // Password change state
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  // Delete account state
  const [delOpen, setDelOpen] = useState(false);
  const [delPhrase, setDelPhrase] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  useEffect(() => {
    api.get("/student/profile").then((r) => {
      setProfile(r.data);
      setForm({
        full_name: r.data.full_name || "",
        contact_number: r.data.contact_number || "",
        school: r.data.school || "",
      });
    }).catch(() => toast.error("Could not load profile"))
      .finally(() => setLoading(false));
  }, []);

  const dirty = profile && (
    form.full_name.trim() !== (profile.full_name || "") ||
    form.contact_number.trim() !== (profile.contact_number || "") ||
    form.school.trim() !== (profile.school || "")
  );

  const saveProfile = async () => {
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    setSaving(true);
    try {
      const r = await api.patch("/student/profile", {
        full_name: form.full_name.trim(),
        contact_number: form.contact_number.trim(),
        school: form.school.trim(),
      });
      setProfile({ ...profile, ...r.data.user });
      toast.success("Profile updated");
      try { setUser?.((u) => (u ? { ...u, ...r.data.user } : u)); } catch { /* noop */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update profile");
    } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pwNew.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pwNew !== pwConfirm) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/student/profile/password", {
        current_password: pwCurrent,
        new_password: pwNew,
      });
      toast.success("Password changed successfully");
      setPwOpen(false);
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally { setPwBusy(false); }
  };

  // The Confirm Delete button enables ONLY when the user types the literal string "DELETE".
  // No trim, no toLowerCase — strict equality is required (matches backend validation).
  const canDelete = delPhrase === "DELETE";

  const confirmDelete = async () => {
    if (!canDelete) return;
    setDelBusy(true);
    try {
      await api.post("/student/account/delete", { confirmation: "DELETE" });
      toast.success("Your account has been deleted. You will now be logged out.");
      // Give the user 1.5s to see the toast before logout/redirect.
      setTimeout(() => {
        try { logout?.(); } catch { /* noop */ }
        nav("/login", { replace: true });
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete account");
      setDelBusy(false);
    }
  };

  if (loading || !profile) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500" data-testid="profile-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading profile…
      </div>
    );
  }

  const initial = (profile.full_name || "?").charAt(0).toUpperCase();
  const isDeleted = profile.status === "deleted";

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="student-profile-page">
      <header>
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-600 dark:text-blue-400">Account</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">My Profile</h1>
        <p className="text-slate-600 dark:text-slate-300 mt-1">Manage your account information, security, and preferences.</p>
      </header>

      {/* Identity card */}
      <Card className="p-4 sm:p-6 border-slate-200">
        <div className="flex items-start gap-4 sm:gap-5 flex-wrap">
          <div
            className="h-16 w-16 sm:h-20 sm:w-20 shrink-0 rounded-full grid place-items-center text-white font-display font-extrabold text-2xl sm:text-3xl shadow-md"
            style={{ background: "linear-gradient(135deg,#4F46E5,#7C3AED)" }}
            data-testid="profile-avatar"
          >
            {initial}
          </div>
          <div className="min-w-0 flex-1 basis-0">
            <div className="font-display text-xl sm:text-2xl font-bold truncate" data-testid="profile-full-name-display">{profile.full_name}</div>
            <div className="text-sm text-slate-500 dark:text-slate-300 flex items-start gap-1.5 mt-1 min-w-0">
              <Mail className="w-4 h-4 mt-0.5 shrink-0" />
              <span className="break-all min-w-0" data-testid="profile-email">{profile.email}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge variant="outline" className="capitalize" data-testid="profile-status">{profile.status}</Badge>
              {profile.selected_course_name && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-50 dark:text-blue-700 max-w-full whitespace-normal break-words text-left leading-tight">
                  <BookOpen className="w-3 h-3 mr-1 shrink-0" /> <span className="break-words">{profile.selected_course_name}</span>
                </Badge>
              )}
              {profile.auth_provider && profile.auth_provider !== "email" && (
                <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-50 dark:text-slate-700" data-testid="profile-auth-provider">
                  <Chrome className="w-3 h-3 mr-1" /> {profile.auth_provider === "google" ? "Google account" : profile.auth_provider === "apple" ? "Apple account" : "Linked"}
                </Badge>
              )}
              {profile.email_verified && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-50 dark:text-emerald-700" data-testid="profile-email-verified-badge">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Email verified
                </Badge>
              )}
            </div>
          </div>
        </div>
        {/* Sign out row — visible on all screens, primary discoverable logout */}
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Signed in on this device.
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => { logout(); nav("/login"); }}
            className="text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800 dark:text-rose-300 dark:border-rose-800/60 dark:hover:bg-rose-950/40"
            data-testid="profile-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Log out
          </Button>
        </div>
      </Card>

      {/* Profile completion card */}
      <ProfileCompletionCard profile={profile} />

      {/* Editable details */}
      <Card className="p-6 border-slate-200">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <User className="w-4 h-4 text-blue-600" /> Profile Details
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-300 mt-1">Email and enrolled course can only be changed with admin approval.</p>

        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="full_name">Full Name</Label>
            <Input
              id="full_name"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="mt-1.5"
              data-testid="profile-input-full-name"
              maxLength={120}
            />
          </div>
          <div>
            <Label htmlFor="contact_number">Contact Number</Label>
            <Input
              id="contact_number"
              value={form.contact_number}
              onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
              className="mt-1.5"
              placeholder="+63 9XX XXX XXXX"
              data-testid="profile-input-contact"
              maxLength={32}
            />
          </div>
          <div>
            <Label htmlFor="school">School</Label>
            <Input
              id="school"
              value={form.school}
              onChange={(e) => setForm({ ...form, school: e.target.value })}
              className="mt-1.5"
              placeholder="Your school or university"
              data-testid="profile-input-school"
              maxLength={160}
            />
          </div>
          <div className="sm:col-span-2 grid sm:grid-cols-2 gap-4 pt-2">
            <ReadonlyRow icon={Mail} label="Email" value={profile.email} />
            <ReadonlyRow icon={BookOpen} label="Enrolled Course" value={profile.selected_course_name || "—"} />
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <Button
            onClick={saveProfile}
            disabled={!dirty || saving}
            className="bg-[#1E3A8A] hover:bg-[#1E40AF] w-full sm:w-auto"
            data-testid="profile-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </Card>

      {/* Account info */}
      <Card className="p-6 border-slate-200">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" /> Account Info
        </h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
          <ReadonlyRow icon={Calendar} label="Date Registered" value={formatDate(profile.created_at)} testId="profile-registered" />
          <ReadonlyRow icon={Clock} label="Last Login" value={formatDateTime(profile.last_login)} testId="profile-last-login" />
        </div>
      </Card>

      {/* Security */}
      <Card className="p-6 border-slate-200">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-amber-600" /> Security
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">Keep your account safe with a strong password.</p>
        <div className="mt-4">
          <Button variant="outline" onClick={() => setPwOpen(true)} className="w-full sm:w-auto" data-testid="profile-change-password-btn">
            <KeyRound className="w-4 h-4 mr-2" /> Change Password
          </Button>
        </div>
      </Card>

      {/* Appearance */}
      <AppearanceCard />

      {/* Danger Zone */}
      <Card className="p-6 border-2 border-rose-300 bg-rose-50/40 dark:bg-rose-950/30" data-testid="profile-danger-zone">
        <h2 className="font-display font-bold text-lg flex items-center gap-2 text-rose-700">
          <AlertTriangle className="w-5 h-5" /> Danger Zone
        </h2>
        <p className="text-sm text-slate-700 dark:text-slate-200 mt-2">
          Permanently delete your account. Your profile, exam history, review history, badges, and learning data
          will be deactivated and cannot be recovered.
        </p>
        <div className="mt-4">
          <Button
            variant="outline"
            onClick={() => { setDelPhrase(""); setDelOpen(true); }}
            disabled={isDeleted}
            className="w-full sm:w-auto border-rose-300 text-rose-700 hover:bg-rose-100 hover:text-rose-800 dark:border-rose-400 dark:text-rose-300 dark:hover:bg-rose-900/60 dark:hover:text-rose-100"
            data-testid="profile-delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete Account
          </Button>
        </div>
      </Card>

      {/* Change Password Dialog */}
      <Dialog open={pwOpen} onOpenChange={(o) => { if (!pwBusy) setPwOpen(o); }}>
        <DialogContent data-testid="change-password-modal">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one (at least 8 characters).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="pw_current">Current password</Label>
              <Input id="pw_current" type="password" autoComplete="current-password" value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)} data-testid="pw-current-input" />
            </div>
            <div>
              <Label htmlFor="pw_new">New password</Label>
              <Input id="pw_new" type="password" autoComplete="new-password" value={pwNew} onChange={(e) => setPwNew(e.target.value)} data-testid="pw-new-input" />
            </div>
            <div>
              <Label htmlFor="pw_confirm">Confirm new password</Label>
              <Input id="pw_confirm" type="password" autoComplete="new-password" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)} data-testid="pw-confirm-input" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setPwOpen(false)} disabled={pwBusy} className="w-full sm:w-auto" data-testid="pw-cancel-btn">
              Cancel
            </Button>
            <Button onClick={changePassword} disabled={pwBusy || !pwCurrent || pwNew.length < 8 || pwNew !== pwConfirm} className="bg-[#1E3A8A] hover:bg-[#1E40AF] w-full sm:w-auto" data-testid="pw-submit-btn">
              {pwBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={delOpen} onOpenChange={(o) => { if (!delBusy) setDelOpen(o); }}>
        <DialogContent data-testid="delete-account-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Delete Account
            </DialogTitle>
            <DialogDescription className="text-slate-700 dark:text-slate-200 leading-relaxed">
              This action is permanent. Your account, profile information, progress, exam history,
              review history, badges, and learning data may be deleted or permanently deactivated.
              This account cannot be recovered once deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-rose-300 bg-rose-50/60 dark:bg-rose-950/40 px-3 py-2.5 text-sm text-rose-800 dark:text-rose-200">
            To confirm account deletion, type <strong>DELETE</strong> below.
          </div>
          <div>
            <Label htmlFor="delete_phrase" className="sr-only">Type DELETE to confirm</Label>
            <Input
              id="delete_phrase"
              value={delPhrase}
              onChange={(e) => setDelPhrase(e.target.value)}
              placeholder="Type DELETE to confirm"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              data-testid="delete-phrase-input"
              className="font-mono"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDelOpen(false)} disabled={delBusy} className="w-full sm:w-auto" data-testid="delete-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={!canDelete || delBusy}
              className="bg-rose-600 hover:bg-rose-700 text-white w-full sm:w-auto disabled:opacity-50"
              data-testid="delete-confirm-btn"
            >
              {delBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Confirm Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReadonlyRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2.5">
      <div className="h-8 w-8 rounded-md bg-slate-100 dark:bg-slate-800 grid place-items-center text-slate-500">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
        <div className="text-sm text-slate-800 dark:text-slate-100 truncate" data-testid={testId}>{value}</div>
      </div>
    </div>
  );
}

const PROFILE_FIELD_LABELS = {
  full_name: "Full Name",
  email: "Email",
  contact_number: "Contact Number",
  school: "School",
  selected_course_id: "Selected Course",
};

function ProfileCompletionCard() {
  const [completion, setCompletion] = useState(null);
  const [accessState, setAccessState] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/student/profile/completion-status").then((r) => r.data).catch(() => null),
      api.get("/student/access-status").then((r) => r.data).catch(() => null),
    ]).then(([c, a]) => {
      setCompletion(c);
      setAccessState(a);
    });
  }, []);
  if (!completion) return null;
  // Hide the reminder for brand-new students with no course enrolled —
  // they can't pay yet, so the "complete profile before payment" framing
  // doesn't apply. They'll see this card again after picking a course.
  if (accessState && !accessState.has_enrolled_course) return null;
  const complete = completion.is_complete;
  return (
    <Card
      className={`p-5 border-2 ${complete ? "border-emerald-200 bg-emerald-50/60 dark:border-emerald-700 dark:bg-emerald-900/20" : "border-amber-200 bg-amber-50/60 dark:border-amber-700 dark:bg-amber-900/20"}`}
      data-testid="profile-completion-card"
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-lg grid place-items-center shrink-0 ${complete ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-800/50 dark:text-emerald-200" : "bg-amber-100 text-amber-700 dark:bg-amber-800/50 dark:text-amber-200"}`}>
          {complete ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-display font-bold ${complete ? "text-emerald-900 dark:text-emerald-100" : "text-amber-900 dark:text-amber-100"}`}>
            Profile Completion: {complete ? "Complete" : "Incomplete"}
          </div>
          {!complete && completion.missing_fields?.length > 0 && (
            <p className="text-sm text-amber-900 dark:text-amber-100 mt-1">
              Your profile is incomplete. <span className="font-semibold">Missing:</span>{" "}
              {completion.missing_fields.map((f) => PROFILE_FIELD_LABELS[f] || f).join(", ")}
            </p>
          )}
          {!complete && (
            <div className="mt-3">
              <Button asChild size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="profile-card-complete-btn">
                <Link to="/student/profile/complete?next=/student/profile">
                  Complete Profile <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}


function AppearanceCard() {
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";

  const resetIntros = () => {
    // Wipe every dismissed page-intro flag (keyed as `pm_intro_v1_<pageKey>_<user_id>`)
    // and every one-off tour flag (LimitedAccessTour uses `pm_la_tour_v1_<user_id>`).
    // After clicking, the intro cards on Study Hub / Enrollments / Analytics will
    // reappear on the next visit — great for onboarding a returning student to
    // a UX change or refreshing an admin's memory.
    let cleared = 0;
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((k) => {
        if (k.startsWith("pm_intro_v1_") || k.startsWith("pm_la_tour_v1_")) {
          localStorage.removeItem(k);
          cleared += 1;
        }
      });
    } catch { /* ignore quota / privacy-mode failures */ }
    if (cleared > 0) {
      toast.success(`Reset ${cleared} tip${cleared === 1 ? "" : "s"} — visit Study, Analytics, or Enrollments to see them again.`, { duration: 6000 });
    } else {
      toast.info("No tips to reset — you haven't dismissed any yet.");
    }
  };

  return (
    <Card className="p-6 border-slate-200 dark:border-slate-800" data-testid="profile-appearance-card">
      <h2 className="font-display font-bold text-lg flex items-center gap-2">
        <Palette className="w-4 h-4 text-violet-600" /> Appearance
      </h2>
      <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
        Choose how PrepMasters looks on this device. Night Mode is easier on your eyes for late-night reviews.
      </p>
      <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm">
          <div className="font-semibold text-slate-900 dark:text-slate-100">
            Currently: {isDark ? "Night Mode" : "Light Mode"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Tap to switch — takes effect immediately.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant={isDark ? "outline" : "default"}
            onClick={() => { if (isDark) toggle(); }}
            className={!isDark ? "bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" : ""}
            data-testid="profile-theme-light-btn"
          >
            Light
          </Button>
          <Button
            type="button"
            variant={isDark ? "default" : "outline"}
            onClick={() => { if (!isDark) toggle(); }}
            className={isDark ? "bg-slate-800 hover:bg-slate-700 text-amber-300 border-slate-700" : ""}
            data-testid="profile-theme-dark-btn"
          >
            Night
          </Button>
          <ThemeToggle />
        </div>
      </div>

      {/* Show-tips-again row */}
      <div className="mt-5 pt-4 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm min-w-0">
          <div className="font-semibold text-slate-900 dark:text-slate-100">Show me the tips again</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Bring back the friendly intro cards on Study, Analytics, and Billing so you can re-read what changed.
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={resetIntros}
          className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-950/40 shrink-0"
          data-testid="profile-reset-intros-btn"
        >
          <RefreshCcw className="w-4 h-4 mr-2" /> Reset Tips
        </Button>
      </div>
    </Card>
  );
}
